// Shapes exchanged with the friendship + presence endpoints. The older duel payloads in this folder are
// untyped maps; new code types its own.

/**
 * Window event DuelSocialLayer rebroadcasts every `/{profileId}/friends` push onto, carrying a
 * {@link FriendEventPayload} in `detail`. The subscription lives in the app shell (mounted once), while the
 * panel that renders the friends is mounted only while open — this is how the two talk without lifting the
 * whole friends list into a context. Same idiom as OPEN_FRIENDS_PANEL_EVENT.
 */
export const FRIEND_EVENT = "cu:friend-event";

/**
 * What a friend is doing right now. Mirrors the backend EPresenceStatus. Coarse on purpose — enough to know
 * whether interrupting is rude, never which lesson they are on.
 */
export type PresenceStatus =
  | "ONLINE"
  | "SOLVING"
  | "IN_DUEL"
  | "IN_EXAM"
  | "OFFLINE";

/** The viewer's relationship to another learner, from the viewer's side. */
export type FriendshipState =
  | "NONE"
  | "PENDING_OUTGOING"
  | "PENDING_INCOMING"
  | "ACCEPTED";

/** A row in the friends list: catchtivity's presence projection, merged with management's friendship id. */
export interface FriendPeer {
  id: number;
  name?: string;
  userDTO?: { firstName?: string; lastName?: string };
  branchDTO?: { name?: string; gradeDTO?: { name?: string } };
  mascotInventory?: any[];
  online?: boolean;
  status?: PresenceStatus;
  /** Filled in from the management friend list; absent until that fetch lands. */
  friendshipId?: number;
}

/** A learner in a pending request or an add-a-friend search result. */
export interface FriendCandidate {
  userProfileId: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  gradeName?: string;
  branchName?: string;
  friendshipId?: number;
  friendshipState?: FriendshipState;
  createdAt?: number;
}

export interface PendingFriendships {
  incomingList: FriendCandidate[];
  outgoingList: FriendCandidate[];
}

/** Payload on the per-user `/{profileId}/friends` topic. */
export interface FriendEventPayload {
  eventType:
    | "REQUEST_RECEIVED"
    | "REQUEST_ACCEPTED"
    | "FRIEND_REMOVED"
    | "STATUS_CHANGED";
  friendshipId?: number;
  counterpartUserProfileId?: number;
  counterpartName?: string;
  /** STATUS_CHANGED only: whose status changed, and to what. */
  subjectUserProfileId?: number;
  status?: PresenceStatus;
}

/** Only a friend with no other claim on their attention may be pulled into a duel. */
export const isInterruptible = (peer: FriendPeer): boolean =>
  Boolean(peer.online) && (peer.status ?? "ONLINE") === "ONLINE";
